import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Tip from "@/components/Tip";
import { Banknote, CheckCircle2, Clock, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function PaymentLedgerPanel({ clinicId, onChanged }) {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ entries: [], totals: {} });
  const [busy, setBusy] = useState(false);

  const load = (status) => api.get(`/clinics/${clinicId}/payment-ledger?status=${status}`)
    .then(r => setData(r.data));

  useEffect(() => { load(tab); }, [tab, clinicId]); // eslint-disable-line

  const runReminders = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clinics/${clinicId}/payment-ledger/run-reminders`);
      toast.success(`Reminders sent · WhatsApp ${r.data.whatsapp_sent} · Email ${r.data.email_sent}`);
      load(tab); onChanged?.();
    } catch { toast.error("Failed to run reminders"); }
    finally { setBusy(false); }
  };

  const markPaid = async (invId) => {
    try {
      await api.post(`/clinics/${clinicId}/invoices/${invId}/mark-paid`);
      toast.success("Marked paid · clinician emailed");
      load(tab); onChanged?.();
    } catch { toast.error("Mark paid failed"); }
  };

  const totals = data.totals || {};

  return (
    <section className="solid-panel p-6 shadow-3d">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-bold flex items-center">
            <Banknote className="h-5 w-5 mr-2 text-[#f37021]" />
            Payment Ledger
            <Tip text="Real-time view of paid & pending invoices. Run automated WhatsApp + Email reminders for pending dues." />
          </h2>
          <p className="text-sm text-slate-500 mt-1">Paid vs Pending · automated reminder cron</p>
        </div>
        <Button onClick={runReminders} disabled={busy} className="bg-[#004a99] hover:bg-[#003a7a] text-white" data-testid="run-reminders-btn">
          <Send className="h-4 w-4 mr-2" /> {busy ? "Sending…" : "Run Reminders Now"}
        </Button>
      </div>

      {/* totals strip */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="ledger-paid-strip">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold flex items-center"><CheckCircle2 className="h-3 w-3 mr-1" /> Paid</div>
          <div className="text-2xl font-bold text-emerald-900">{fmtINR(totals.paid_amount)}</div>
          <div className="text-xs text-emerald-700">{totals.paid_count || 0} invoices</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ledger-pending-strip">
          <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold flex items-center"><Clock className="h-3 w-3 mr-1" /> Pending</div>
          <div className="text-2xl font-bold text-amber-900">{fmtINR(totals.pending_amount)}</div>
          <div className="text-xs text-amber-700">{totals.pending_count || 0} invoices</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
          <TabsTrigger value="pending" data-testid="ledger-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="paid" data-testid="ledger-tab-paid">Paid</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-3">
          {data.entries.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              {tab === "pending" ? "✓ No pending dues — your clinic is fully settled" : "No paid invoices yet"}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Reminders</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.entries.map((e) => (
                  <TableRow key={e.id} data-testid={`ledger-row-${e.id}`}>
                    <TableCell>
                      <div className="font-semibold text-slate-900">{e.patient_name}</div>
                      <div className="text-xs text-slate-500">{e.patient_phone}</div>
                    </TableCell>
                    <TableCell className="font-semibold text-[#004a99]">{fmtINR(e.amount)}</TableCell>
                    <TableCell className="text-sm">
                      {e.due_at?.slice(0, 10)}
                      {e.status === "overdue" && <span className="ml-2 text-red-600 inline-flex items-center"><AlertTriangle className="h-3 w-3 mr-1" />overdue</span>}
                    </TableCell>
                    <TableCell className="text-sm">{e.reminder_count || 0} sent</TableCell>
                    <TableCell className="text-right">
                      {e.status !== "paid" && (
                        <Button size="sm" onClick={() => markPaid(e.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`mark-paid-${e.id}`}>
                          Mark Paid
                        </Button>
                      )}
                      {e.status === "paid" && <span className="text-xs text-emerald-700 font-semibold">✓ Paid</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>
    </section>
  );
}
